import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest, TokenManager } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/layout/Header';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { initializeFromStoredTokens } = useAuth();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Prevent duplicate verification attempts (React StrictMode calls useEffect twice in dev)
    if (verificationAttempted.current) {
      return;
    }
    verificationAttempted.current = true;

    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setError('Invalid verification link. No token provided.');
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/verify-email?token=${token}`, {
          method: 'GET',
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || errorData.message || 'Verification failed');
        }

        const data = await response.json();

        // Store the tokens received from verification endpoint
        if (data.access_token && data.refresh_token) {
          const tokenManager = TokenManager.getInstance();
          tokenManager.storeTokens(data.access_token, data.refresh_token);

          // Initialize auth context with the new tokens
          try {
            await initializeFromStoredTokens();
          } catch (initError) {
            console.error('Failed to initialize auth after verification:', initError);
            setStatus('error');
            setError('Email verified but failed to initialize session. Please try logging in.');
            return;
          }
        }

        setStatus('success');

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          console.log("Redirecting to dashboard...");
          navigate('/dashboard');
        }, 2000);
      } catch (error: any) {
        setStatus('error');
        setError(error.message || 'Failed to verify email. Please try again.');
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const handleResendVerification = async () => {
    const token = searchParams.get('token');

    if (!token) {
      setResendMessage('Invalid verification link. Please sign up again.');
      return;
    }

    setResendLoading(true);
    setResendMessage('');

    try {
      const response = await apiRequest('/resend-verification-email', {
        method: 'POST',
        body: JSON.stringify({ token })
      });

      setResendMessage(response.message || 'Verification email sent! Please check your inbox.');
    } catch (error: any) {
      setResendMessage(error.message || 'Failed to resend verification email.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-background">
      <div className="flex h-full grow flex-col">
        <Header variant="auth" />

        <div className="px-40 flex flex-1 justify-center py-5">
          <div className="flex flex-col w-[512px] max-w-[512px] py-5 max-w-[960px] flex-1">
            <div className="text-center py-8">
              {status === 'verifying' && (
                <>
                  <div className="mb-6">
                    <div className="w-20 h-20 mx-auto border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 pb-3">
                    Verifying your email...
                  </h2>
                  <p className="text-text-secondary text-base px-4">
                    Please wait while we verify your email address.
                  </p>
                </>
              )}

              {status === 'success' && (
                <>
                  <div className="mb-6">
                    <svg className="w-20 h-20 mx-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 pb-3">
                    Email verified!
                  </h2>
                  <p className="text-text-secondary text-base px-4 pb-6">
                    Your account has been successfully verified.
                  </p>
                  <p className="text-text-secondary text-sm px-4 pb-8">
                    Redirecting you to your dashboard...
                  </p>
                </>
              )}

              {status === 'error' && (
                <>
                  <div className="mb-6">
                    <svg className="w-20 h-20 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 pb-3">
                    Verification failed
                  </h2>
                  <p className="text-red-400 text-base px-4 pb-6">
                    {error}
                  </p>

                  {resendMessage && (
                    <div className={`mx-4 mb-4 p-3 rounded text-sm max-w-[450px] mx-auto ${resendMessage.includes('sent') || resendMessage.includes('resent') ? 'bg-green-500/20 border border-green-500/30 text-green-300' : 'bg-red-500/20 border border-red-500/30 text-red-300'}`}>
                      {resendMessage}
                    </div>
                  )}

                  <div className="flex flex-col gap-3 px-4">
                    <button
                      onClick={handleResendVerification}
                      disabled={resendLoading}
                      className="flex max-w-[450px] w-full mx-auto cursor-pointer items-center justify-center overflow-hidden rounded h-12 px-5 bg-primary text-white text-base font-bold leading-normal tracking-[0.015em] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
